import { DEFAULT_USER_DETAILS } from '@common/constants'
import { CClient, showErrorAlert } from '@common/utils'
import RatelimitErrorModal from '@components/auth/ratelimit-error-modal.dynamic'
import CompetitionRegistrationComponent from '@components/competitions/competition-registration-component'
import {
  prepareRegistrationFormBody,
  registerUser,
  requestLoginOtp,
} from '@containers/auth/utils'
import {
  validateSchoolData,
  validateCompetitionRegistrationFormData,
} from '@containers/book-trial-class/validators'
import { validateDynamicFormData } from '@containers/competitions/validators'
import { UserProps, useSessionContext } from '@contexts/session'
import {
  generateUTMParametersDictFromCookie,
  getUTMParametersDictFromLocalStorage,
} from '@lib/analytics/track-utm'
import {
  CODING_COMPETITION_SLUGS,
  HPE_CODEWARS_2024_SLUGS,
} from '@lib/constants/competitions'
import { ROLES } from '@lib/enums/dashboard'
import { FB_EVENTS } from '@lib/enums/tracking'
import { RecaptchaType } from '@lib/types/recaptcha'
import { getAPIErrorString } from '@lib/utils/common'
import { captureException } from '@sentry/node'
import $ from 'jquery'
import { useRouter } from 'next/router'
import React, { FormEvent, useEffect, useRef, useState } from 'react'
import swal from 'sweetalert'
import AuthModal from '@containers/competitions/auth-modal.dynamic'
import useSegment, {
  getSegmentTrackingDict,
  SegmentEventEnum,
} from '@hooks/useSegment'
import { t } from '@lingui/macro'
import { competitionCourseMapping } from '@lib/constants/booking'
import cogoToast from 'cogo-toast'
import { updateUserSchool } from '@common/utils'
import date from '@lib/utils/date.utils'

// Add competition slug which should redirect to thank you page after registration
export const COMPETITION_SLUG_TO_THANKYOU_PAGE: string[] = [
  'cyber-genius-sudoku-competition',
  'cyber-genius-2023-msw-logo-1',
  'cyber-genius-2023-ms-paint-competition',
  'cyber-genius2023-scratch-competition',
  'hpe-codewars-2025',
]

type Props = {
  next?: string
  formData: string
  slug: string
  phone?: string
  nextRedirect?: string
  title?: string
  start: string
  end: string
}

export const CompetitionRegistrationContainer = ({
  formData,
  slug,
  phone: newUserPhone,
  nextRedirect,
  title,
  start,
  end,
}: Props) => {
  const {
    loggedIn,
    name,
    email,
    phone,
    student,
    id: userId,
  } = useSessionContext()
  const dynamicFormRef = useRef(null)
  const router = useRouter()
  const recaptchaRef = useRef<RecaptchaType>()
  const [signUpData, setSignUpData] = useState({
    // User form inputs
    phone: newUserPhone || phone || '',
    name: name && name !== DEFAULT_USER_DETAILS.name ? name : '',
    email:
      email && !email.includes(DEFAULT_USER_DETAILS.emailDomain) ? email : '',
    role: ROLES.STUDENT,
    childName: '',
    childGrade: {
      value: student?.grade || 'select',
      label: student?.grade || 'Select grade/class',
    },
    childGender: { value: null, label: 'Select gender' },
    hasComputer: true,
    termsAndConditions: true,
    whatsappEnabled: CODING_COMPETITION_SLUGS.includes(
      router.query?.competitionName as string
    )
      ? false
      : true,
    signupSource: router.asPath.split('?')[0],

    // Server response
    response: {},
    errors: {},
    loading: false,
  })

  const [showRatelimitModal, setShowRatelimitModal] = useState(false)
  const [schoolId, setSchoolId] = useState<number>(null)
  const [schoolName, setSchoolName] = useState<string>('')
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [hasUpcomingClass, setHasUpcomingClass] = useState(false)
  const track = useSegment()

  // Confirm if user has upcoming course class
  const getUpcomingClass = async () => {
    try {
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&limit=1&is_trial=False&student_id=${student.id}`
      )
      if (!response.ok) throw new Error(response.statusText)

      const data = await response.json()
      if (data.length > 0) setHasUpcomingClass(true)
    } catch (error) {
      captureException(error)
    }
  }

  useEffect(() => {
    const updatedEmail =
      (signUpData.email === '' ||
        signUpData.email.includes('@codingal-student.com')) &&
      signUpData.phone
        ? `${signUpData.phone}@codingal-student.com`
        : signUpData.email
    changeSignUpData({ email: updatedEmail })
  }, [signUpData.phone])

  useEffect(() => {
    if (loggedIn && student?.id) {
      getUpcomingClass()
    }
  }, [loggedIn, student?.id])

  const changeSignUpData = (data) => {
    setSignUpData((prevData) => ({ ...prevData, ...data }))
  }

  const handleInputChange = (elem: React.FormEvent<HTMLInputElement>): void => {
    changeSignUpData({
      [elem.currentTarget.name]: elem.currentTarget.value,
    })
  }

  const handlePhoneInputChange = (value: string) => {
    changeSignUpData({
      phone: value,
    })
  }

  const handleSelectChange = (
    id: string,
    value: {
      value: string
      label: string
    }
  ) => {
    changeSignUpData({
      [id]: value,
    })
  }

  const getDynamicFormData = () => {
    const formRenderInstance = $(dynamicFormRef.current).formRender('userData')
    const formBody = {}
    formRenderInstance.map((obj) => {
      if (typeof obj.userData === 'undefined') obj.userData = ['']
      formBody[obj.name] = obj.userData && obj.userData[0]
    })
    return formBody
  }

  const getRequiredDynamicFormFields = () => {
    const formRenderInstance = $(dynamicFormRef.current).formRender('userData')
    const requiredFields = []
    formRenderInstance.map((obj) => {
      obj.required && requiredFields.push(obj.name)
    })
    return requiredFields
  }

  const showDynamicErrors = (errors) => {
    const previousErrorElements = document.getElementsByClassName(
      'custom-error'
    )
    while (previousErrorElements.length > 0) previousErrorElements[0].remove()

    const errorsArray = Object.entries(errors)
    errorsArray.map((element) => {
      const [name, value] = element
      const elem = document.querySelector(`[name=${name}]`)
      const parentDiv = elem.parentElement
      const errorNode = document.createElement('div')
      errorNode.classList.add(
        'text-red-200',
        'text-sm',
        'leading-4',
        'font-400',
        'py-2',
        'custom-error'
      )
      errorNode.innerHTML = `${value}`
      parentDiv.appendChild(errorNode)
    })
  }

  // This only fires for logged-out users trying to register
  const handleCaptcha = async (key) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!key) return

    // Else reCAPTCHA was executed successfully so proceed with the signup
    const formBody = prepareRegistrationFormBody(signUpData)
    formBody['recaptchaResponse'] = key
    // Reset recaptcha so that user can take it again next time
    recaptchaRef.current.reset()

    try {
      const result = await registerUser(formBody)

      if (result.ok) {
        registerCompetition()

        if (window.fbq) {
          const data: UserProps = await result.json()
          window.fbq(
            'trackCustom',
            FB_EVENTS.SIGNUP_COMPLETED,
            {},
            { eventID: data?.id?.toString() }
          )
          track(
            SegmentEventEnum.CONVERSION_SIGNUP_COMPLETED,
            getSegmentTrackingDict(data)
          )
          await updateUserSchool(data.id, schoolId, schoolName)
        }
        if (!window.fbq) {
          const { id: registeredUserId } = await result.json()
          await updateUserSchool(registeredUserId, schoolId, schoolName)
        }
      } else if (result.status === 429) {
        setShowRatelimitModal(true)
        changeSignUpData({ loading: false })
      } else if (result.status === 403) {
        showErrorAlert({
          text: t`You have been blocked. Please contact support if you think it is a mistake.`,
        })
        changeSignUpData({ loading: false })
      } else {
        const error = await result.json()
        changeSignUpData({
          errors: error,
          loading: false,
        })
      }
    } catch (error) {
      changeSignUpData({ loading: false })
      swal({
        icon: 'error',
        title: 'Sorry!',
        text:
          'There was error signing you up. Please refresh the page and try again.',
      })
      captureException(error)
    }
  }

  const handleRegisterForLoggedOutUser = async () => {
    try {
      const response = await requestLoginOtp({ phone: `+${signUpData.phone}` })
      if (response.status === 200) {
        setIsAuthModalOpen(true)
      } else if (response.status === 204) {
        await recaptchaRef.current.execute()
      } else {
        const error = await response.json()
        changeSignUpData({
          errors: error,
          loading: false,
        })
      }
    } catch (error) {
      changeSignUpData({ loading: false })
      swal({
        icon: 'error',
        title: 'Sorry!',
        text:
          'There was error signing you up. Please refresh the page and try again.',
      })
      captureException(error)
    }
  }

  const handleRegisterForLoggedInUser = async () => {
    try {
      await updateUser()
      await updateUserSchool(userId, schoolId, schoolName)
      await registerCompetition()
    } catch (error) {
      changeSignUpData({ loading: false })
      swal({
        icon: 'error',
        title: 'Sorry!',
        text:
          'There was error registering for competition. Please refresh the page and try again.',
      })
      captureException(error)
    }
  }

  const handleSignUpFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (signUpData.loading) return

    changeSignUpData({
      loading: true,
    })

    const updatedPhoneNo = signUpData?.phone?.startsWith?.('+')
      ? signUpData?.phone?.slice?.(1)
      : signUpData?.phone

    const schoolFieldErrors = validateSchoolData(schoolName)
    const errors = validateCompetitionRegistrationFormData({
      ...signUpData,
      phone: updatedPhoneNo || '',
    })
    const dynamicFormBody = getDynamicFormData()
    const dynamicFormRequiredFields = getRequiredDynamicFormFields()
    const valuesArray = Object.entries(dynamicFormBody)
    const dynamicFormErrors = validateDynamicFormData(
      valuesArray,
      dynamicFormRequiredFields
    )
    showDynamicErrors(dynamicFormErrors)

    // Validate form data
    const allErrors = {
      ...errors,
      ...dynamicFormErrors,
      ...schoolFieldErrors,
    }
    if (Object.keys(allErrors).length) {
      changeSignUpData({
        errors: allErrors,
        loading: false,
      })
      return
    }

    loggedIn
      ? await handleRegisterForLoggedInUser()
      : await handleRegisterForLoggedOutUser()
  }

  const registerCompetition = async () => {
    const dynamicFormBody = getDynamicFormData()
    const formBody = {
      data: dynamicFormBody,
    }
    const url = `/api/v1/competitions/${slug}/register/`
    const response = await CClient(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formBody),
    })

    if (response.ok) {
      if (window.fbq) {
        const eventSlug =
          slug === HPE_CODEWARS_2024_SLUGS.CODE_BATTLE
            ? FB_EVENTS.HPE_CODEBATTLE_REGISTRATION_COMPLETED
            : title.replace(/\s+/g, '') + 'RegistrationCompleted'
        window.fbq('trackCustom', eventSlug)
      }
      const isCodeBattle = CODING_COMPETITION_SLUGS.includes(slug)
      const currentTime = date()
      const timeDifference = date(isCodeBattle ? start : end).diff(
        currentTime,
        'seconds'
      )
      let path = `/competitions/${slug}`
      // If code battle check is start time in less than 24 hrs, redirect to competition page
      // If hackathon check is end time in less than 24hrs, redirect to submit page
      // If code battle and event started, redirect to problems page
      if (isCodeBattle && timeDifference <= 0) {
        path = `/competitions/${slug}/problems`
      } else if (timeDifference <= 86400) {
        path = isCodeBattle
          ? `/competitions/${slug}`
          : `/competitions/${slug}/submit`
      } else {
        // Redirect to thank you page if user has upcoming class or competition is in thank you page list. else redirect to trial class booking page
        path =
          COMPETITION_SLUG_TO_THANKYOU_PAGE.includes(slug) || hasUpcomingClass
            ? nextRedirect || `/competitions/${slug}/register/complete`
            : `/trial-class/booking/`
        router.query.course = competitionCourseMapping[slug] || 'coding'
      }

      router.push({
        pathname: path,
        query: router.query,
      })
      // Registration success toast message
      cogoToast.success(
        <div className="flex items-center space-x-2">
          <p className="lg:text-lg">
            You have successfully registered for the{' '}
            <a
              className="font-600 text-blue-300 underline"
              href={`/competitions/${slug}`}
            >
              {title}
            </a>
          </p>
        </div>,
        { hideAfter: 15 }
      )
    } else {
      throw new Error(response.statusText)
    }
  }

  const updateUser = async () => {
    const { name: formName, childGrade: formGrade } = signUpData

    // lead source information
    let leadSourceDict = getUTMParametersDictFromLocalStorage()
    if (!leadSourceDict) leadSourceDict = generateUTMParametersDictFromCookie()

    const body = {
      name: formName,
      email: signUpData.email,
      recentLeadSource: leadSourceDict,
      student: { id: student.id, grade: formGrade.value },
    }

    const url = '/api/v1/users/me/'

    if (!student?.id) {
      throw new Error('Student ID can not be null')
    }

    const response = await CClient(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (response.ok) return
    else {
      const error = await response.json()
      changeSignUpData({
        errors: error,
      })
      throw new Error(await getAPIErrorString(response, url, body))
    }
  }

  return (
    <>
      <CompetitionRegistrationComponent
        {...signUpData}
        recaptchaRef={recaptchaRef}
        handleCaptcha={handleCaptcha}
        handleInputChange={handleInputChange}
        handlePhoneInputChange={handlePhoneInputChange}
        handleSelectChange={handleSelectChange}
        handleSignUpFormSubmit={handleSignUpFormSubmit}
        loggedIn={loggedIn}
        formData={formData}
        dynamicFormRef={dynamicFormRef}
        setSchoolName={setSchoolName}
        setSchoolId={setSchoolId}
        schoolName={schoolName}
      />
      <AuthModal
        isOpen={isAuthModalOpen}
        setIsOpen={setIsAuthModalOpen}
        handleOtpVerified={registerCompetition}
        phone={signUpData.phone}
      />
      <RatelimitErrorModal
        isOpen={showRatelimitModal}
        setIsOpen={setShowRatelimitModal}
      />
    </>
  )
}
